/**
 * Variant availability + swatch media for any product option setup
 */
(function () {
  const SWATCH_SELECTOR =
    'label.color-swatch, label.block-swatch, label.thumbnail-swatch, a.color-swatch, a.block-swatch, a.thumbnail-swatch';

  const matrixByHandle = Object.create(null);
  const AUTO_SELECT_DONE_ATTR = 'data-auto-selected-first-available';

  function escapeAttr(s) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
    return String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function findPickerByFormId(formId) {
    if (!formId) return null;
    return document.querySelector('variant-picker[form-id="' + escapeAttr(formId) + '"]');
  }

  function norm(s) {
    return typeof s === 'string' ? s.trim() : s;
  }

  function buildIdToNameMap(matrix) {
    const idToName = new Map();
    for (const opt of matrix.options) {
      for (const v of opt.values) {
        idToName.set(String(v.id), norm(v.name));
      }
    }
    return idToName;
  }

  function optionAt(variant, position, optionCount) {
    const o = variant.options;
    if (!o || o.length !== optionCount) return undefined;
    return norm(o[position - 1]);
  }

  function isVariantMatchForPartialSelection(
    variant,
    position,
    valueName,
    selectedByPosition,
    idToName,
    optionCount
  ) {
    const vn = norm(valueName);
    if (optionAt(variant, position, optionCount) !== vn) return false;

    for (const [pos, valueId] of selectedByPosition) {
      if (pos === position) continue;
      const selectedName = idToName.get(String(valueId));
      if (!selectedName) return false;
      if (optionAt(variant, pos, optionCount) !== norm(selectedName)) return false;
    }
    return true;
  }

  function isCombinationAvailable(matrix, idToName, position, valueName, selectedByPosition, optionCount) {
    for (const variant of matrix.variants) {
      if (!variant.available) continue;
      if (
        isVariantMatchForPartialSelection(variant, position, valueName, selectedByPosition, idToName, optionCount)
      ) {
        return true;
      }
    }
    return false;
  }

  function findVariantForSwatchMedia(matrix, idToName, position, valueName, selectedByPosition, optionCount) {
    for (const variant of matrix.variants) {
      if (
        isVariantMatchForPartialSelection(variant, position, valueName, selectedByPosition, idToName, optionCount)
      ) {
        return variant;
      }
    }
    for (const variant of matrix.variants) {
      if (optionAt(variant, position, optionCount) === norm(valueName)) return variant;
    }
    return null;
  }

  function styleHasImageUrl(el) {
    if (!el) return false;
    const s = el.getAttribute('style') || '';
    return s.includes('url(');
  }

  function updateSwatchMediaForInput(input, variant, visual) {
    const media = variant && variant.featured_media;
    if (!media || !media.src) return;

    const img = visual.querySelector('img');
    if (img) {
      img.src = media.src;
      if (media.srcset) {
        img.srcset = media.srcset;
        if (!img.sizes) img.sizes = '(max-width: 699px) 48px, 68px';
      } else {
        img.removeAttribute('srcset');
      }
      return;
    }

    // Swatch background images should not be dynamically replaced with the variant's featured image.
    // This allows custom uploaded swatches to persist and color swatches to remain their defined colors.
  }

  function getSelectedByPosition(picker) {
    const selected = new Map();
    const inputs = picker.querySelectorAll('input[data-option-position]:checked');
    for (const input of inputs) {
      const pos = parseInt(input.getAttribute('data-option-position'), 10);
      if (!Number.isNaN(pos)) selected.set(pos, input.value);
    }
    return selected;
  }

  function findVisualControl(input) {
    const next = input.nextElementSibling;
    if (next && next.matches && next.matches(SWATCH_SELECTOR)) return next;
    const parent = input.parentElement;
    if (parent && parent.matches && parent.matches('label.popover__value-option')) return parent;
    return null;
  }

  function setDisabledState(input, disabled) {
    const visual = findVisualControl(input);
    input.disabled = disabled;
    if (visual) {
      visual.classList.toggle('is-disabled', disabled);
      if (disabled) visual.setAttribute('inert', '');
      else visual.removeAttribute('inert');
    }
  }

  function validateMatrix(matrix) {
    if (!matrix || !Array.isArray(matrix.variants) || !Array.isArray(matrix.options)) return false;
    const declared = matrix.option_count != null ? Number(matrix.option_count) : matrix.options.length;
    if (!Number.isFinite(declared) || declared < 1) return false;
    if (matrix.options.length !== declared) return false;
    return true;
  }

  function parseMatrixFromPicker(picker) {
    const matrixScript = picker.querySelector('script[type="application/json"][data-variant-matrix]');
    const handle = picker.getAttribute('handle');
    let matrix = null;
    if (matrixScript && matrixScript.textContent && matrixScript.textContent.trim()) {
      try {
        matrix = JSON.parse(matrixScript.textContent);
      } catch {
        matrix = null;
      }
    }
    if (matrix && validateMatrix(matrix) && handle) {
      matrixByHandle[handle] = matrix;
    } else if ((!matrix || !validateMatrix(matrix)) && handle && matrixByHandle[handle]) {
      matrix = matrixByHandle[handle];
    }
    return matrix && validateMatrix(matrix) ? matrix : null;
  }

  function getSelectedVariantFromPicker(picker) {
    const variantScript = picker.querySelector('script[data-variant]');
    if (!variantScript || !variantScript.textContent || !variantScript.textContent.trim()) return null;

    try {
      return JSON.parse(variantScript.textContent);
    } catch {
      return null;
    }
  }

  function findFirstAvailableVariantKeepingFirstOption(matrix, currentVariant) {
    if (!matrix || !Array.isArray(matrix.variants)) return null;
    if (!currentVariant || !currentVariant.options || !currentVariant.options.length) return null;

    const firstOptionValue = norm(currentVariant.options[0]);

    for (const variant of matrix.variants) {
      if (!variant || !variant.available || !variant.options || !variant.options.length) continue;
      if (norm(variant.options[0]) === firstOptionValue) {
        return variant;
      }
    }

    return null;
  }

  function buildNameToIdMapsByPosition(matrix) {
    const maps = [];
    if (!matrix || !Array.isArray(matrix.options)) return maps;

    for (let i = 0; i < matrix.options.length; i++) {
      const opt = matrix.options[i];
      const m = new Map();

      if (opt && Array.isArray(opt.values)) {
        for (const v of opt.values) {
          if (!v) continue;
          m.set(norm(v.name), String(v.id));
        }
      }

      maps.push(m);
    }

    return maps;
  }

  function tryAutoSelectFirstAvailable(picker) {
    if (!picker || picker.hasAttribute(AUTO_SELECT_DONE_ATTR)) return;
    if (!picker.hasAttribute('update-url')) return;

    const matrix = parseMatrixFromPicker(picker);
    if (!matrix) return;

    const selectedVariant = getSelectedVariantFromPicker(picker);
    if (!selectedVariant) return;

    if (selectedVariant.available !== false) return;

    const firstAvailableWithSameColor = findFirstAvailableVariantKeepingFirstOption(matrix, selectedVariant);
    
    if (!firstAvailableWithSameColor || !firstAvailableWithSameColor.options || !firstAvailableWithSameColor.options.length) {
      return;
    }
    
    if (firstAvailableWithSameColor.id && selectedVariant.id && String(firstAvailableWithSameColor.id) === String(selectedVariant.id)) {
      return;
    }

    const formId = picker.getAttribute('form-id');
    if (!formId) return;

    const nameToIdByPos = buildNameToIdMapsByPosition(matrix);
    const optionValueIds = [];

    for (let i = 0; i < firstAvailableWithSameColor.options.length; i++) {
      const valueName = norm(firstAvailableWithSameColor.options[i]);
      const valueId = nameToIdByPos[i] && nameToIdByPos[i].get(valueName);
      if (!valueId) return;
      optionValueIds.push(valueId);
    }

    picker.setAttribute(AUTO_SELECT_DONE_ATTR, '');

    for (let i = 0; i < optionValueIds.length; i++) {
      const position = i + 1;
      const valueId = optionValueIds[i];
      const input = document.querySelector(
        `input[form="${escapeAttr(formId)}"][data-option-position="${position}"][value="${escapeAttr(valueId)}"]`
      );

      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  function applyMatrix(picker) {
    if (picker.querySelector('input[data-product-url]')) return;

    const matrix = parseMatrixFromPicker(picker);
    if (!matrix) return;

    const optionCount = matrix.options.length;
    const idToName = buildIdToNameMap(matrix);
    const inputs = picker.querySelectorAll('input[data-option-position]');
    if (!inputs.length) return;

    const selectedByPosition = getSelectedByPosition(picker);

    for (const input of inputs) {
      const pos = parseInt(input.getAttribute('data-option-position'), 10);
      if (Number.isNaN(pos) || pos < 1 || pos > optionCount) continue;

      const valueName = idToName.get(String(input.value));
      if (!valueName) continue;

      if (input.checked) {
        setDisabledState(input, false);
        continue;
      }

      const available = isCombinationAvailable(
        matrix,
        idToName,
        pos,
        valueName,
        selectedByPosition,
        optionCount
      );
      setDisabledState(input, !available);
    }

    const hasAnyMedia = matrix.variants.some((v) => v.featured_media && v.featured_media.src);
    if (hasAnyMedia) {
      for (const input of inputs) {
        const pos = parseInt(input.getAttribute('data-option-position'), 10);
        if (Number.isNaN(pos) || pos < 1 || pos > optionCount) continue;
        const valueName = idToName.get(String(input.value));
        if (!valueName) continue;
        const visual = findVisualControl(input);
        if (!visual) continue;
        const mediaVariant = findVariantForSwatchMedia(
          matrix,
          idToName,
          pos,
          valueName,
          selectedByPosition,
          optionCount
        );
        updateSwatchMediaForInput(input, mediaVariant, visual);
      }
    }
  }

  function refreshAll() {
    document.querySelectorAll('variant-picker[form-id]').forEach(applyMatrix);
  }

  function scheduleApplyPicker(picker) {
    if (!picker) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => applyMatrix(picker));
    });
  }

  function refreshPickerFromEvent(target) {
    const picker = target && target.closest && target.closest('variant-picker[form-id]');
    if (picker) scheduleApplyPicker(picker);
  }

  function bindProductFormRerender(form) {
    if (!(form instanceof HTMLFormElement) || form.dataset.availabilityMatrixRerender) return;
    form.dataset.availabilityMatrixRerender = 'true';
    form.addEventListener('product:rerender', () => {
      const formId = form.getAttribute('id');
      scheduleApplyPicker(findPickerByFormId(formId));
    });
  }

  function bindAllProductForms() {
    document.querySelectorAll('form[id][action*="/cart/add"]').forEach(bindProductFormRerender);
  }

  function init() {
    bindAllProductForms();
    refreshAll();

    setTimeout(() => {
      document.querySelectorAll('variant-picker[form-id][update-url]').forEach(tryAutoSelectFirstAvailable);
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener(
    'change',
    (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (!t.matches('input[data-option-position]')) return;
      refreshPickerFromEvent(t);
    },
    true
  );

  document.addEventListener('variant:change', (e) => {
    const form = e.target;
    let picker = null;
    if (form instanceof HTMLFormElement) {
      const id = form.getAttribute('id');
      picker = findPickerByFormId(id);
    }
    if (!picker && e.detail && e.detail.formId != null) {
      picker = findPickerByFormId(String(e.detail.formId));
    }
    if (picker) {
      scheduleApplyPicker(picker);
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(refreshAll);
      });
    }
  });

  let bindFormsDebounce;
  new MutationObserver(() => {
    clearTimeout(bindFormsDebounce);
    bindFormsDebounce = setTimeout(() => {
      bindAllProductForms();
      refreshAll();
    }, 250);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();